import { ChangeEvent, FC, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  Alert,
  AlertTitle,
  alpha,
  Button,
  CircularProgress,
  Snackbar,
  Typography,
} from '@mui/material';
import { ProfilePicture } from './SetProfilePicture';
import { Link } from '@vs-online-only-app/ui';

const ProfilePictureButton = styled(Button)`
  border-radius: 50%;
  width: 128px;
  height: 128px;
  border: 1px solid ${({ theme }) => theme.palette.divider};
  position: relative;
  overflow: hidden;
  background-color: ${({ theme }) => theme.palette.background.default};
  margin: 0 auto;
  padding: 0;
` as typeof Button;

const ProfileImage = styled.img`
  width: 100%;
  object-fit: cover;
`;

const Overlay = styled.div`
  position: absolute;
  bottom: 0;
  height: 40px;
  width: 100%;
  background: ${({ theme }) => alpha(theme.palette.background.paper, 0.9)};
  padding-top: ${({ theme }) => theme.spacing(1)};
`;

const VisuallyHiddenInput = styled.input`
  position: absolute;
  top: 0;
  right: 0;
  width: 1px;
  height: 1px;
  text-indent: -999px;
  opacity: 0;
`;

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (reader.result) {
        resolve(reader.result.toString());
      } else {
        reject(new Error('File reading failed'));
      }
    };
    reader.onerror = () => {
      reject(new Error('Error reading file'));
    };
  });

const validImageFormats = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
];

type Props = {
  imgSrc?: string;
  setProfilePicture: (picture: ProfilePicture) => void;
  isUploading?: boolean;
  disabled?: boolean;
};

const UploadProfilePicture: FC<Props> = ({
  imgSrc,
  setProfilePicture,
  isUploading,
  disabled,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState(false);

  const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    inputRef.current?.blur();

    const file: File = Array.from(event.target.files as ArrayLike<File>)[0];
    const fileType = file.type;

    try {
      const base64 = await fileToBase64(file);
      setProfilePicture({ base64, fileType });
    } catch (e) {
      setError(true);
      console.error(e);
    }

    event.target.value = '';
  };

  return (
    <>
      <Snackbar
        open={error}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        autoHideDuration={10000}
        onClose={() => setError(false)}
      >
        <Alert severity="error">
          <AlertTitle>Unable to upload file</AlertTitle>
          Please try again. If the problem persists, please{' '}
          <Link
            sx={{ cursor: 'pointer' }}
            to="mailto:support-chat@bambuser.com"
          >
            contact customer care.
          </Link>
        </Alert>
      </Snackbar>
      <ProfilePictureButton
        disabled={disabled}
        role={undefined}
        component="label"
        tabIndex={-1}
      >
        <VisuallyHiddenInput
          disabled={isUploading} // Prevent initiating a paralell upload
          ref={inputRef}
          type="file"
          title="Upload profile picture"
          accept={validImageFormats.join(',')}
          onChange={handleChange}
        />
        {imgSrc && <ProfileImage alt="profile" src={imgSrc} />}
        {!disabled && (
          <Overlay>
            <Typography
              variant="body2"
              color="textSecondary"
              textAlign="center"
            >
              {isUploading ? (
                <CircularProgress color="inherit" size={20} />
              ) : imgSrc ? (
                'Change'
              ) : (
                'Upload'
              )}
            </Typography>
          </Overlay>
        )}
      </ProfilePictureButton>
    </>
  );
};

export default UploadProfilePicture;
